import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DictationInput from '../../components/DictationInput';
import { 
  Scale, Calendar, Plus, Clock, User, Building, FileText,
  AlertCircle, CheckCircle, XCircle, Edit, Trash2, Eye,
  CalendarDays, Gavel, Bell
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const CASE_TYPES = [
  { id: 'probation', name: 'Probation' },
  { id: 'parole', name: 'Parole' },
  { id: 'diversion', name: 'Diversion Program' },
  { id: 'drug_court', name: 'Drug Court' },
  { id: 'mental_health_court', name: 'Mental Health Court' },
  { id: 'veterans_court', name: "Veterans' Court" },
  { id: 'other', name: 'Other' },
];

const CASE_STATUSES = [
  { id: 'active', name: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'pending', name: 'Pending', color: 'bg-amber-100 text-amber-700' },
  { id: 'completed', name: 'Completed', color: 'bg-sky-100 text-sky-700' },
  { id: 'dismissed', name: 'Dismissed', color: 'bg-slate-100 text-slate-700' },
  { id: 'revoked', name: 'Revoked', color: 'bg-red-100 text-red-700' },
  { id: 'transferred', name: 'Transferred', color: 'bg-violet-100 text-violet-700' },
];

const OUTCOMES = [
  { id: 'pending', name: 'Pending' },
  { id: 'successful_completion', name: 'Successful Completion' },
  { id: 'early_termination', name: 'Early Termination' },
  { id: 'revocation', name: 'Revocation' },
  { id: 'new_charges', name: 'New Charges' },
  { id: 'absconded', name: 'Absconded' },
  { id: 'transferred', name: 'Transferred' },
  { id: 'dismissed', name: 'Dismissed' },
];

export default function CourtCasesPage() {
  const { user, axiosAuth } = useAuth();
  const [cases, setCases] = useState([]);
  const [upcomingDates, setUpcomingDates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [newCase, setNewCase] = useState({
    participant_id: '',
    case_type: '',
    case_number: '',
    court_name: '',
    judge_name: '',
    probation_officer: '',
    start_date: '',
    end_date: '',
    next_court_date: '',
    charges: '',
    conditions: '',
    notes: '',
  });
  const [newDate, setNewDate] = useState({
    case_id: '',
    date: '',
    time: '09:00',
    purpose: '',
    location: '',
    notes: '',
  });
  const api = axiosAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [casesRes, upcomingRes, summaryRes, usersRes] = await Promise.all([
        api.get('/court/cases'),
        api.get('/court/upcoming?days=14'),
        api.get('/court/summary'),
        api.get('/users?role=client,housing_resident'),
      ]);
      setCases(casesRes.data.cases || []);
      setUpcomingDates(upcomingRes.data.dates || []);
      setSummary(summaryRes.data);
      setParticipants(usersRes.data.users || []);
    } catch (err) {
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateCase = async () => {
    if (!newCase.participant_id || !newCase.case_type) {
      toast.error('Please select participant and case type');
      return;
    }
    try {
      const params = new URLSearchParams();
      Object.entries(newCase).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      await api.post(`/court/cases?${params.toString()}`);
      toast.success('Court case created!');
      setAddDialogOpen(false);
      setNewCase({
        participant_id: '', case_type: '', case_number: '', court_name: '',
        judge_name: '', probation_officer: '', start_date: '', end_date: '',
        next_court_date: '', charges: '', conditions: '', notes: '',
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to create case');
    }
  };

  const handleAddDate = async () => {
    if (!newDate.case_id || !newDate.date) {
      toast.error('Please select case and date');
      return;
    }
    try {
      const params = new URLSearchParams();
      Object.entries(newDate).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      await api.post(`/court/dates?${params.toString()}`);
      toast.success('Court date added!');
      setDateDialogOpen(false);
      setNewDate({ case_id: '', date: '', time: '09:00', purpose: '', location: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to add court date');
    }
  };

  const viewCase = async (caseId) => {
    try {
      const res = await api.get(`/court/cases/${caseId}`);
      setSelectedCase(res.data);
      setViewDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load case details');
    }
  };

  const getStatusBadge = (status) => {
    const s = CASE_STATUSES.find(st => st.id === status);
    return s ? <Badge className={s.color}>{s.name}</Badge> : <Badge variant="outline">{status}</Badge>;
  };

  const getCaseTypeName = (type) => {
    const t = CASE_TYPES.find(ct => ct.id === type);
    return t ? t.name : type;
  };

  const getDaysUntil = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const diff = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading && !cases.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-slate-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="court-cases-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Scale className="text-slate-700" /> Court Case Management
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Track court cases, dates, and outcomes
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setDateDialogOpen(true)} variant="outline" className="rounded-full gap-2">
            <CalendarDays size={16} /> Add Date
          </Button>
          <Button onClick={() => setAddDialogOpen(true)} className="bg-slate-700 hover:bg-slate-800 text-white rounded-full gap-2">
            <Plus size={16} /> New Case
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Scale className="text-slate-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold">{summary.total_cases}</p>
              <p className="text-xs text-muted-foreground">Total Cases</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="text-emerald-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold">{summary.active_cases}</p>
              <p className="text-xs text-muted-foreground">Active Cases</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center">
                  <FileText className="text-sky-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold">{summary.completed_cases}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Calendar className="text-amber-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold">{summary.upcoming_dates}</p>
              <p className="text-xs text-muted-foreground">Upcoming Dates</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="cases" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="cases" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            All Cases ({cases.length})
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Upcoming Dates ({upcomingDates.length})
          </TabsTrigger>
        </TabsList>

        {/* Cases Tab */}
        <TabsContent value="cases" className="space-y-4">
          {cases.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <Scale className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No court cases found.</p>
                <Button onClick={() => setAddDialogOpen(true)} className="mt-4 bg-slate-700 hover:bg-slate-800 text-white rounded-full gap-2">
                  <Plus size={16} /> Create First Case
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {cases.map(c => (
                <Card key={c.id} className="border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow cursor-pointer" onClick={() => viewCase(c.id)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                          <Gavel className="text-slate-600" size={24} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{c.participant_name}</h3>
                            {getStatusBadge(c.status)}
                            <Badge variant="outline">{getCaseTypeName(c.case_type)}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {c.case_number && <span className="mr-3">Case #: {c.case_number}</span>}
                            {c.court_name && <span>{c.court_name}</span>}
                          </p>
                          {c.next_court_date && (
                            <div className="flex items-center gap-1 mt-2 text-sm">
                              <Calendar size={14} className="text-amber-500" />
                              <span className="text-amber-700 font-medium">
                                Next Court: {formatDate(c.next_court_date)}
                                {getDaysUntil(c.next_court_date) <= 7 && getDaysUntil(c.next_court_date) >= 0 && (
                                  <span className="ml-1 text-amber-600">({getDaysUntil(c.next_court_date)} days)</span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="shrink-0">
                        <Eye size={18} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Upcoming Dates Tab */}
        <TabsContent value="upcoming" className="space-y-4">
          {upcomingDates.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No upcoming court dates in the next 14 days.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {upcomingDates.map(d => {
                const daysUntil = getDaysUntil(d.date);
                return (
                  <Card key={d.id} className={`border-0 shadow-sm rounded-2xl ${daysUntil <= 3 ? 'border-l-4 border-l-amber-500' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${daysUntil <= 3 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                            <Calendar className={daysUntil <= 3 ? 'text-amber-600' : 'text-slate-600'} size={24} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{d.participant_name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(d.date)} at {d.time} • {d.location || 'Location TBD'}
                            </p>
                            {d.purpose && <p className="text-sm mt-1">{d.purpose}</p>}
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={daysUntil <= 1 ? 'bg-red-100 text-red-700' : daysUntil <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>
                            {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Case Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>New Court Case</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Participant *</label>
              <Select value={newCase.participant_id} onValueChange={(v) => setNewCase({...newCase, participant_id: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select participant..." />
                </SelectTrigger>
                <SelectContent>
                  {participants.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Case Type *</label>
                <Select value={newCase.case_type} onValueChange={(v) => setNewCase({...newCase, case_type: v})}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CASE_TYPES.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Case Number</label>
                <Input 
                  value={newCase.case_number}
                  onChange={(e) => setNewCase({...newCase, case_number: e.target.value})}
                  placeholder="e.g., CR-2026-12345"
                  className="rounded-xl"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Court Name</label>
              <Input 
                value={newCase.court_name}
                onChange={(e) => setNewCase({...newCase, court_name: e.target.value})}
                placeholder="e.g., Harris County Criminal Court"
                className="rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Judge Name</label>
                <Input 
                  value={newCase.judge_name}
                  onChange={(e) => setNewCase({...newCase, judge_name: e.target.value})}
                  placeholder="Judge name"
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Probation/Parole Officer</label>
                <Input 
                  value={newCase.probation_officer}
                  onChange={(e) => setNewCase({...newCase, probation_officer: e.target.value})}
                  placeholder="Officer name"
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Start Date</label>
                <Input 
                  type="date"
                  value={newCase.start_date}
                  onChange={(e) => setNewCase({...newCase, start_date: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">End Date</label>
                <Input 
                  type="date"
                  value={newCase.end_date}
                  onChange={(e) => setNewCase({...newCase, end_date: e.target.value})}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Next Court Date</label>
              <Input 
                type="date"
                value={newCase.next_court_date}
                onChange={(e) => setNewCase({...newCase, next_court_date: e.target.value})}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Charges</label>
              <DictationInput 
                value={newCase.charges}
                onChange={(val) => setNewCase({...newCase, charges: val})}
                placeholder="List charges... (speak or type)"
                rows={2}
                showCleanup={false}
                data-testid="case-charges-dictation"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Conditions</label>
              <DictationInput 
                value={newCase.conditions}
                onChange={(val) => setNewCase({...newCase, conditions: val})}
                placeholder="Probation/parole conditions... (speak or type)"
                rows={2}
                showCleanup={false}
                data-testid="case-conditions-dictation"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Notes</label>
              <DictationInput 
                value={newCase.notes}
                onChange={(val) => setNewCase({...newCase, notes: val})}
                placeholder="Additional notes... (speak or type)"
                rows={2}
                showCleanup={false}
                data-testid="case-notes-dictation"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleCreateCase} className="bg-slate-700 hover:bg-slate-800 text-white rounded-full gap-2">
              <Plus size={16} /> Create Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Court Date Dialog */}
      <Dialog open={dateDialogOpen} onOpenChange={setDateDialogOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Add Court Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Case *</label>
              <Select value={newDate.case_id} onValueChange={(v) => setNewDate({...newDate, case_id: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select case..." />
                </SelectTrigger>
                <SelectContent>
                  {cases.filter(c => c.status === 'active').map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.participant_name} - {c.case_number || getCaseTypeName(c.case_type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Date *</label>
                <Input 
                  type="date"
                  value={newDate.date}
                  onChange={(e) => setNewDate({...newDate, date: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Time</label>
                <Input 
                  type="time"
                  value={newDate.time}
                  onChange={(e) => setNewDate({...newDate, time: e.target.value})}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Purpose</label>
              <Input 
                value={newDate.purpose}
                onChange={(e) => setNewDate({...newDate, purpose: e.target.value})}
                placeholder="e.g., Status hearing, Review"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Location</label>
              <Input 
                value={newDate.location}
                onChange={(e) => setNewDate({...newDate, location: e.target.value})}
                placeholder="Court address or room"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Notes</label>
              <DictationInput 
                value={newDate.notes}
                onChange={(val) => setNewDate({...newDate, notes: val})}
                placeholder="Any notes... (speak or type)"
                rows={2}
                showCleanup={false}
                data-testid="court-date-notes-dictation"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDateDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleAddDate} className="bg-slate-700 hover:bg-slate-800 text-white rounded-full gap-2">
              <Plus size={16} /> Add Date
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Case Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Case Details</DialogTitle>
          </DialogHeader>
          {selectedCase && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Gavel className="text-slate-600" size={28} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedCase.participant_name}</h3>
                  <div className="flex gap-2 mt-1">
                    {getStatusBadge(selectedCase.status)}
                    <Badge variant="outline">{getCaseTypeName(selectedCase.case_type)}</Badge>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                {selectedCase.case_number && (
                  <div className="flex justify-between p-2 bg-muted/30 rounded-lg text-sm">
                    <span className="text-muted-foreground">Case Number</span>
                    <span className="font-medium">{selectedCase.case_number}</span>
                  </div>
                )}
                {selectedCase.court_name && (
                  <div className="flex justify-between p-2 bg-muted/30 rounded-lg text-sm">
                    <span className="text-muted-foreground">Court</span>
                    <span className="font-medium">{selectedCase.court_name}</span>
                  </div>
                )}
                {selectedCase.judge_name && (
                  <div className="flex justify-between p-2 bg-muted/30 rounded-lg text-sm">
                    <span className="text-muted-foreground">Judge</span>
                    <span className="font-medium">{selectedCase.judge_name}</span>
                  </div>
                )}
                {selectedCase.probation_officer && (
                  <div className="flex justify-between p-2 bg-muted/30 rounded-lg text-sm">
                    <span className="text-muted-foreground">Officer</span>
                    <span className="font-medium">{selectedCase.probation_officer}</span>
                  </div>
                )}
                {selectedCase.start_date && (
                  <div className="flex justify-between p-2 bg-muted/30 rounded-lg text-sm">
                    <span className="text-muted-foreground">Start Date</span>
                    <span className="font-medium">{formatDate(selectedCase.start_date)}</span>
                  </div>
                )}
                {selectedCase.end_date && (
                  <div className="flex justify-between p-2 bg-muted/30 rounded-lg text-sm">
                    <span className="text-muted-foreground">End Date</span>
                    <span className="font-medium">{formatDate(selectedCase.end_date)}</span>
                  </div>
                )}
              </div>

              {selectedCase.charges && (
                <div className="p-3 bg-muted/30 rounded-xl">
                  <p className="text-sm font-medium mb-1">Charges</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedCase.charges}</p>
                </div>
              )}

              {selectedCase.conditions && (
                <div className="p-3 bg-muted/30 rounded-xl">
                  <p className="text-sm font-medium mb-1">Conditions</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedCase.conditions}</p>
                </div>
              )}

              {selectedCase.court_dates?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Court Dates</p>
                  <div className="space-y-2">
                    {selectedCase.court_dates.map(d => (
                      <div key={d.id} className="p-2 bg-amber-50 rounded-lg text-sm flex justify-between items-center">
                        <div>
                          <span className="font-medium">{formatDate(d.date)}</span>
                          <span className="text-muted-foreground ml-2">at {d.time}</span>
                          {d.purpose && <span className="text-muted-foreground ml-2">• {d.purpose}</span>}
                        </div>
                        <Badge variant="outline" className="text-xs">{d.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
